import React, { useState } from "react"
import { Link, useSearch, useRouterState } from "@tanstack/react-router"
import { useResetPassword, useResetTokenUser } from "@/lib/queries"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { PasswordValidation, validatePassword } from "@/components/PasswordValidation"

export function ResetPasswordPage() {
  const { reset_password_token } = useSearch({ strict: false }) as {
    reset_password_token?: string
  }
  const routerState = useRouterState()
  const isAdmin = routerState.location.pathname.startsWith("/specia_admins/")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const resetPassword = useResetPassword(isAdmin)
  const tokenUser = useResetTokenUser(isAdmin ? undefined : reset_password_token)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reset_password_token) return

    resetPassword.mutate({
      reset_password_token,
      password,
      password_confirmation: passwordConfirmation,
    })
  }

  if (!reset_password_token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-sm">
          <CardHeader className="flex flex-col items-center text-center">
            <img src="/signature_logo.png" alt="Specia" className="h-10 mb-2" />
            <p className="text-sm text-muted-foreground">
              Invalid or missing password reset link.
            </p>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild variant="link">
              <Link to={isAdmin ? "/admin/login" : "/login"}>Go to login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (resetPassword.isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-sm">
          <CardHeader className="flex flex-col items-center text-center">
            <img src="/signature_logo.png" alt="Specia" className="h-10 mb-2" />
            <p className="text-sm text-muted-foreground">
              Your password has been set successfully.
            </p>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild>
              <Link to={isAdmin ? "/admin/login" : "/login"}>Sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col items-center text-center">
          <img src="/signature_logo.png" alt="Specia" className="h-10 mb-2" />
          <p className="text-sm text-muted-foreground">
            Set your password to get started
          </p>
        </CardHeader>
        <CardContent>
          {tokenUser?.data?.username && (
            <p className="mb-4 text-center text-sm text-muted-foreground">
              Username: <span className="font-medium text-foreground">{tokenUser.data.username}</span>
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">Confirm Password</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>
            {resetPassword.isError && (
              <p className="text-sm text-destructive">
                {resetPassword.error instanceof Error
                  ? resetPassword.error.message
                  : "Failed to set password"}
              </p>
            )}
            <PasswordValidation
              password={password}
              passwordConfirmation={passwordConfirmation}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={
                !validatePassword(password, passwordConfirmation) || resetPassword.isPending
              }
            >
              {resetPassword.isPending ? "Setting password..." : "Set password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
